import React, { useEffect, useState, useCallback, useRef } from "react";
import {
  Box,
  TextField,
  Typography,
  Tooltip,
  Divider,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Paper,
  Chip,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import FactoryOutlinedIcon from "@mui/icons-material/FactoryOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DirectionsBoatOutlinedIcon from "@mui/icons-material/DirectionsBoatOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

import { useTheme, alpha } from "@mui/material/styles";
import {
  getLogisticsByTaskId,
  getLogisticsByProductId,
  updateLogisticsEntry,
  addLogisticsEntry,
} from "../../api/Logistica/logisticsApi";
import { getPresupuestoInfoByProductId } from "../../api/Estimates/estimatesApi";
import {
  getFileContent,
  uploadProjectFile,
  deleteFile,
} from "../../api/Proyectos/projectFilesApi";
import LogisticaDocThumbnail from "../Logistica/LogisticaDocThumbnail";

const fieldStyleSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    fontSize: "0.8rem",
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.8rem",
  },
};

const DateField = ({ field, label, value, onChange }) => (
  <Box sx={{ width: "100%" }}>
    <DatePicker
      label={label}
      value={value ? dayjs(value) : null}
      format="DD/MM/YYYY"
      onChange={onChange}
      slotProps={{
        actionBar: { actions: ["clear"] },
        textField: {
          size: "small",
          fullWidth: true,
          sx: fieldStyleSx,
        },
      }}
      sx={{ width: "100%" }}
    />
  </Box>
);

function EditableLegacyText({
  entry,
  field,
  label,
  multiline = false,
  onPatch,
  datalistOptions = null,
}) {
  const [local, setLocal] = useState(() =>
    entry[field] == null ? "" : String(entry[field]),
  );
  const listId =
    Array.isArray(datalistOptions) && datalistOptions.length
      ? `legacy-dl-${field}-${entry.Id}`
      : null;

  useEffect(() => {
    setLocal(entry[field] == null ? "" : String(entry[field]));
  }, [entry.Id, field, entry[field]]);

  const commit = async () => {
    const prev = entry[field] == null ? "" : String(entry[field]);
    if (local === prev) return;
    const v = local.trim() === "" ? null : local;
    await onPatch(entry, { [field]: v });
  };

  return (
    <Tooltip title={label} placement="top">
      <Box sx={{ width: "100%" }}>
        {listId ? (
          <datalist id={listId}>
            {datalistOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        ) : null}
        <TextField
          label={label}
          value={local}
          multiline={multiline}
          minRows={multiline ? 2 : 1}
          type="text"
          autoComplete="off"
          size="small"
          fullWidth
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          inputProps={listId ? { list: listId } : undefined}
          sx={fieldStyleSx}
        />
      </Box>
    </Tooltip>
  );
}

function EditableLegacyNumber({ entry, field, label, integer, onPatch }) {
  const [local, setLocal] = useState(() =>
    entry[field] == null || entry[field] === "" ? "" : String(entry[field]),
  );

  useEffect(() => {
    setLocal(
      entry[field] == null || entry[field] === "" ? "" : String(entry[field]),
    );
  }, [entry.Id, field, entry[field]]);

  const commit = async () => {
    const prev =
      entry[field] == null || entry[field] === ""
        ? ""
        : String(entry[field]);
    const trimmed = local.trim();
    if (trimmed === "") {
      if (prev === "") return;
      await onPatch(entry, { [field]: null });
      return;
    }
    if (integer) {
      const n = parseInt(trimmed, 10);
      const out = Number.isFinite(n) ? n : null;
      if (String(entry[field] ?? "") === String(out ?? "")) return;
      await onPatch(entry, { [field]: out });
      return;
    }
    const n = Number(String(trimmed).replace(",", "."));
    const out = Number.isFinite(n) ? n : null;
    if (String(entry[field] ?? "") === String(out ?? "")) return;
    await onPatch(entry, { [field]: out });
  };

  return (
    <Tooltip title={label} placement="top">
      <Box sx={{ width: "100%" }}>
        <TextField
          label={label}
          value={local}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          size="small"
          fullWidth
          onChange={(e) => setLocal(e.target.value)}
          onBlur={commit}
          sx={fieldStyleSx}
        />
      </Box>
    </Tooltip>
  );
}

const logisticsQueryOpts = (idPresupuesto, idPresupuestoA3Pedido = null) => {
  if (
    idPresupuestoA3Pedido != null &&
    Number.isFinite(Number(idPresupuestoA3Pedido))
  ) {
    return { idPresupuestoA3Pedido: Number(idPresupuestoA3Pedido) };
  }
  if (idPresupuesto != null && Number.isFinite(Number(idPresupuesto))) {
    return { idPresupuesto: Number(idPresupuesto) };
  }
  return {};
};

/** Cabecera de sub-grupo dentro de cada tarjeta */
const SubGroup = ({ icon, title, accent, children }) => {
  const theme = useTheme();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 0.5 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "6px",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            color: accent,
            backgroundColor: alpha(accent, 0.14),
            border: "1px solid",
            borderColor: alpha(accent, 0.3),
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: 14 } })}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            color: theme.palette.text.secondary,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        {children}
      </Box>
    </Box>
  );
};

const LogisticsSection = ({
  timelineTaskId,
  productId,
  idPresupuesto = null,
  idPresupuestoA3Pedido = null,
  numeroPresupuesto = null,
  legacyLogistics = [],
  onDirty,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [logistics, setLogistics] = useState([]);
  const [factories, setFactories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addingLegacy, setAddingLegacy] = useState(false);
  const [projectFiles, setProjectFiles] = useState([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [uploadBusy, setUploadBusy] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (Array.isArray(legacyLogistics) && legacyLogistics.length > 0) {
      setLogistics(legacyLogistics);
    }
  }, [legacyLogistics]);

  useEffect(() => {
    if (!productId && !timelineTaskId) return;

    const fetchLogistics = async () => {
      setLoading(true);
      try {
        let data = [];
        if (productId) {
          data = await getLogisticsByProductId(
            productId,
            logisticsQueryOpts(idPresupuesto, idPresupuestoA3Pedido),
          );
        }
        if ((!Array.isArray(data) || data.length === 0) && timelineTaskId) {
          data = await getLogisticsByTaskId(timelineTaskId);
        }
        setLogistics((prev) =>
          Array.isArray(data) && data.length > 0 ? data : prev,
        );
      } catch (err) {
        console.error("❌ Error loading logistics:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchLogistics();
  }, [timelineTaskId, productId, idPresupuesto, idPresupuestoA3Pedido]);

  useEffect(() => {
    if (!productId) return;

    const loadFactories = async () => {
      try {
        const info = await getPresupuestoInfoByProductId(
          productId,
          logisticsQueryOpts(idPresupuesto, idPresupuestoA3Pedido),
        );
        if (!info?.ProveedorNombre) return;

        const res = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/api/productionTimeline/proveedor/${encodeURIComponent(
            info.ProveedorNombre,
          )}/fabricas`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        const result = await res.json();
        setFactories(
          result.map((name) => ({
            id: name,
            name,
          })),
        );
      } catch (err) {
        console.error("❌ Error loading factories:", err);
      }
    };

    loadFactories();
  }, [productId, idPresupuesto, idPresupuestoA3Pedido]);

  const refreshProjectFiles = useCallback(async () => {
    if (!numeroPresupuesto || String(numeroPresupuesto).trim() === "") {
      setProjectFiles([]);
      return;
    }
    setFilesLoading(true);
    try {
      const data = await getFileContent(String(numeroPresupuesto).trim());
      setProjectFiles(Array.isArray(data) ? data : []);
    } catch {
      setProjectFiles([]);
    } finally {
      setFilesLoading(false);
    }
  }, [numeroPresupuesto]);

  useEffect(() => {
    refreshProjectFiles();
  }, [refreshProjectFiles]);

  const handleAddLegacyLine = useCallback(async () => {
    if (!productId) return;
    setAddingLegacy(true);
    try {
      const payload = {
        productId: Number(productId),
        ...(Number.isFinite(Number(idPresupuesto)) && Number(idPresupuesto) > 0
          ? { idPresupuesto: Number(idPresupuesto) }
          : {}),
        ...(Number.isFinite(Number(timelineTaskId)) && Number(timelineTaskId) > 0
          ? { TimelineTaskId: Number(timelineTaskId) }
          : {}),
      };
      const created = await addLogisticsEntry(payload);
      setLogistics((prev) => [...(Array.isArray(prev) ? prev : []), created]);
      onDirty && onDirty();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.message ||
        "No se pudo crear la línea de logística.";
      console.error(err);
      window.alert(msg);
    } finally {
      setAddingLegacy(false);
    }
  }, [productId, idPresupuesto, timelineTaskId, onDirty]);

  const handleProjectFileInput = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !idPresupuesto) return;
    setUploadBusy(true);
    try {
      await uploadProjectFile(file, idPresupuesto);
      await refreshProjectFiles();
      onDirty && onDirty();
    } catch (err) {
      console.error(err);
      window.alert(
        err?.response?.data?.error ||
          err?.message ||
          "Error al subir el archivo.",
      );
    } finally {
      setUploadBusy(false);
    }
  };

  const handleDeleteProjectFile = async (fileName) => {
    if (!numeroPresupuesto || !window.confirm(`¿Eliminar «${fileName}»?`)) return;
    const filePath = `Proyectos/${String(numeroPresupuesto).trim()}/${fileName}`;
    try {
      await deleteFile(filePath);
      await refreshProjectFiles();
      onDirty && onDirty();
    } catch (err) {
      console.error(err);
      window.alert(
        err?.response?.data?.error || err?.message || "Error al eliminar.",
      );
    }
  };

  const isPdfFile = (f) =>
    /^application\/pdf$/i.test(String(f?.mimeType || "")) ||
    /\.pdf$/i.test(String(f?.fileName || ""));

  const openProjectFile = (file) => {
    if (!file?.data) return;
    try {
      const href = `data:${file.mimeType};base64,${file.data}`;
      if (isPdfFile(file)) {
        window.open(href, "_blank", "noopener,noreferrer");
        return;
      }
      const a = document.createElement("a");
      a.href = href;
      a.download = file.fileName;
      a.click();
    } catch (err) {
      console.error(err);
    }
  };

  const handleLegacyPatch = useCallback(
    async (entry, patch) => {
      try {
        await updateLogisticsEntry(entry.Id, patch);
        setLogistics((prev) =>
          prev.map((x) => (x.Id === entry.Id ? { ...x, ...patch } : x)),
        );
        onDirty && onDirty();
      } catch (err) {
        console.error("❌ Error actualizando logística legacy:", err);
      }
    },
    [onDirty],
  );

  const handleLegacyDateChange = useCallback(
    async (entry, field, dateValue) => {
      const payload = {
        [field]: dateValue ? dayjs(dateValue).format("YYYY-MM-DD") : null,
      };
      await handleLegacyPatch(entry, payload);
    },
    [handleLegacyPatch],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
          py: 6,
        }}
      >
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary" fontWeight={600}>
          Cargando logística…
        </Typography>
      </Box>
    );
  }

  const rows = (logistics || []).filter((e) => !e?._fromLogisticaEnvio);

  const accents = {
    production: theme.palette.info.main,
    cargo: theme.palette.warning.main,
    shipment: theme.palette.primary.main,
    tracking: theme.palette.success.main,
    documents: theme.palette.secondary.main,
  };

  return (
    <Stack spacing={2.25}>
      {/* 🔹 Header de la sección */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              color: theme.palette.primary.contrastText,
              flexShrink: 0,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            }}
          >
            <LocalShippingOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography variant="subtitle2" fontWeight={800}>
                Logística legacy
              </Typography>
              <Chip
                label="ProductionLogistics"
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.62rem",
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  backgroundColor: alpha(theme.palette.text.primary, 0.06),
                  color: theme.palette.text.secondary,
                }}
              />
              {rows.length > 0 && (
                <Chip
                  label={`${rows.length} ${rows.length === 1 ? "envío" : "envíos"}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: "0.62rem",
                    fontWeight: 700,
                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    color: theme.palette.primary.main,
                    border: "1px solid",
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                  }}
                />
              )}
            </Stack>
            <Typography
              variant="caption"
              sx={{ display: "block", color: "text.secondary", lineHeight: 1.4 }}
            >
              Todos los campos son editables. Los envíos nuevos siguen en
              «Gestión logística de pedidos».
              {factories.length > 0
                ? " Sugerencias de fábrica del proveedor al escribir en «Factory»."
                : ""}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          size="small"
          startIcon={
            addingLegacy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <AddIcon />
            )
          }
          disabled={!productId || addingLegacy}
          onClick={handleAddLegacyLine}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}`,
            width: { xs: "100%", md: "auto" },
            alignSelf: { xs: "stretch", md: "flex-start" },
            flexShrink: 0,
            whiteSpace: { xs: "normal", md: "nowrap" },
          }}
        >
          {addingLegacy ? "Creando…" : "Añadir línea de logística"}
        </Button>
      </Box>

      {/* 🔹 Estado vacío */}
      {rows.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2.5,
            borderStyle: "dashed",
            borderColor: alpha(theme.palette.primary.main, 0.35),
            backgroundColor: alpha(theme.palette.primary.main, isDark ? 0.06 : 0.03),
            textAlign: "center",
          }}
        >
          <InboxOutlinedIcon
            sx={{ fontSize: 36, color: alpha(theme.palette.primary.main, 0.7), mb: 0.5 }}
          />
          <Typography variant="subtitle2" fontWeight={700}>
            Aún no hay líneas de logística legacy
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
            Si el Critical Path ya existe, crea una fila nueva; quedará enlazada a
            la tarea «Shipment Method».
          </Typography>
        </Paper>
      ) : null}

      {/* 🔹 Tarjetas */}
      {rows.map((entry, idx) => {
        const factoryLabel = entry.Factory || "Sin factory";
        const poLabel = entry.POReference || "Sin PO";
        const etaLabel = entry.ETA
          ? dayjs(entry.ETA).isValid()
            ? dayjs(entry.ETA).format("DD/MM/YYYY")
            : null
          : null;

        return (
          <Paper
            key={entry.Id}
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: theme.palette.background.paper,
              overflow: "hidden",
              transition: "border-color 0.15s ease, box-shadow 0.15s ease",
              "&:hover": {
                borderColor: alpha(theme.palette.primary.main, 0.4),
                boxShadow: `0 6px 18px ${alpha(theme.palette.common.black, isDark ? 0.35 : 0.06)}`,
              },
            }}
          >
            {/* Mini-header de la tarjeta */}
            <Box
              sx={{
                px: 2,
                py: 1.25,
                background: `linear-gradient(135deg, ${alpha(
                  theme.palette.primary.main,
                  isDark ? 0.16 : 0.07,
                )} 0%, ${alpha(theme.palette.background.paper, 0.96)} 100%)`,
                borderBottom: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                icon={<LocalShippingOutlinedIcon fontSize="small" />}
                label={`Envío #${idx + 1}`}
                size="small"
                sx={{
                  fontWeight: 700,
                  height: 22,
                  fontSize: "0.7rem",
                  backgroundColor: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  "& .MuiChip-icon": { color: theme.palette.primary.main },
                }}
              />
              <Tooltip title="Factory">
                <Chip
                  icon={<FactoryOutlinedIcon fontSize="small" />}
                  label={factoryLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    maxWidth: 220,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Tooltip>
              <Tooltip title="PO Reference">
                <Chip
                  icon={<AssignmentOutlinedIcon fontSize="small" />}
                  label={poLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    maxWidth: 220,
                    "& .MuiChip-label": {
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
              </Tooltip>
              {etaLabel && (
                <Tooltip title="ETA">
                  <Chip
                    icon={<EventOutlinedIcon fontSize="small" />}
                    label={`ETA · ${etaLabel}`}
                    size="small"
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: alpha(theme.palette.success.main, 0.14),
                      color: theme.palette.success.main,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.success.main, 0.35),
                      "& .MuiChip-icon": { color: theme.palette.success.main },
                    }}
                  />
                </Tooltip>
              )}
            </Box>

            {/* Cuerpo: subgrupos en grid responsive */}
            <Box
              sx={{
                p: 2,
                display: "grid",
                gap: 2.5,
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, minmax(0, 1fr))",
                  lg: "repeat(3, minmax(0, 1fr))",
                  xl: "repeat(5, minmax(0, 1fr))",
                },
              }}
            >
              <SubGroup
                title="Producción"
                icon={<FactoryOutlinedIcon />}
                accent={accents.production}
              >
                <EditableLegacyText
                  entry={entry}
                  field="Factory"
                  label="Factory"
                  datalistOptions={factories.map((f) => f.name)}
                  onPatch={handleLegacyPatch}
                />
                <EditableLegacyText
                  entry={entry}
                  field="POReference"
                  label="PO Reference"
                  onPatch={handleLegacyPatch}
                />
                <DateField
                  field="PODate"
                  label="PO Date"
                  value={entry.PODate}
                  onChange={(v) => handleLegacyDateChange(entry, "PODate", v)}
                />
              </SubGroup>

              <SubGroup
                title="Carga"
                icon={<Inventory2OutlinedIcon />}
                accent={accents.cargo}
              >
                <EditableLegacyNumber
                  entry={entry}
                  field="NCartons"
                  label="Nº Cartons"
                  integer
                  onPatch={handleLegacyPatch}
                />
                <EditableLegacyNumber
                  entry={entry}
                  field="GrossWeightKgs"
                  label="Gross Weight (kg)"
                  onPatch={handleLegacyPatch}
                />
                <EditableLegacyNumber
                  entry={entry}
                  field="TotalVolumeCbm"
                  label="Volume (cbm)"
                  onPatch={handleLegacyPatch}
                />
              </SubGroup>

              <SubGroup
                title="Envío"
                icon={<DirectionsBoatOutlinedIcon />}
                accent={accents.shipment}
              >
                <EditableLegacyText
                  entry={entry}
                  field="ShipmentMethod"
                  label="Shipment Method"
                  onPatch={handleLegacyPatch}
                />
                <DateField
                  field="BookingDone"
                  label="Booking Done"
                  value={entry.BookingDone}
                  onChange={(v) =>
                    handleLegacyDateChange(entry, "BookingDone", v)
                  }
                />
                <EditableLegacyText
                  entry={entry}
                  field="Forwarder"
                  label="Forwarder"
                  onPatch={handleLegacyPatch}
                />
              </SubGroup>

              <SubGroup
                title="Tracking"
                icon={<AssignmentOutlinedIcon />}
                accent={accents.tracking}
              >
                <EditableLegacyText
                  entry={entry}
                  field="ShipmentCodeCustomer"
                  label="Shipment Code"
                  onPatch={handleLegacyPatch}
                />
                <EditableLegacyText
                  entry={entry}
                  field="BLAWBNr"
                  label="B/L or AWB Nº"
                  onPatch={handleLegacyPatch}
                />
                <DateField
                  field="ETA"
                  label="ETA"
                  value={entry.ETA}
                  onChange={(v) => handleLegacyDateChange(entry, "ETA", v)}
                />
              </SubGroup>

              <SubGroup
                title="Documentos"
                icon={<DescriptionOutlinedIcon />}
                accent={accents.documents}
              >
                <DateField
                  field="DocumentsApproved"
                  label="Docs Approved"
                  value={entry.DocumentsApproved}
                  onChange={(v) =>
                    handleLegacyDateChange(entry, "DocumentsApproved", v)
                  }
                />
                <DateField
                  field="OriginalTelexDocsSemt"
                  label="Telex Docs"
                  value={entry.OriginalTelexDocsSemt}
                  onChange={(v) =>
                    handleLegacyDateChange(entry, "OriginalTelexDocsSemt", v)
                  }
                />
                <EditableLegacyText
                  entry={entry}
                  field="Remarks"
                  label="Remarks"
                  multiline
                  onPatch={handleLegacyPatch}
                />
                <EditableLegacyText
                  entry={entry}
                  field="LinkDocs"
                  label="Link docs"
                  onPatch={handleLegacyPatch}
                />
              </SubGroup>
            </Box>
          </Paper>
        );
      })}

      <Divider sx={{ my: 0.5, opacity: 0.6 }} />

      {/* 🔹 Documentos del presupuesto (carpeta Proyectos — suele ser fotos / adjuntos del presupuesto) */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: theme.palette.background.paper,
          p: 2,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1.25,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: alpha(theme.palette.secondary.main, 0.14),
              color: theme.palette.secondary.main,
              border: "1px solid",
              borderColor: alpha(theme.palette.secondary.main, 0.3),
            }}
          >
            <FolderOpenOutlinedIcon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" fontWeight={800}>
              Documentos del presupuesto
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Archivos compartidos del proyecto
              {numeroPresupuesto ? ` · ${numeroPresupuesto}` : ""}
            </Typography>
          </Box>
        </Stack>

        {!numeroPresupuesto && idPresupuesto ? (
          <Typography variant="body2" color="text.secondary">
            Cargando número de presupuesto…
          </Typography>
        ) : null}

        {numeroPresupuesto ? (
          <>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              onChange={handleProjectFileInput}
            />
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mb: 1.25 }}
              flexWrap="wrap"
            >
              <Button
                size="small"
                variant="outlined"
                startIcon={
                  uploadBusy ? (
                    <CircularProgress size={16} />
                  ) : (
                    <UploadFileIcon />
                  )
                }
                disabled={!idPresupuesto || uploadBusy}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Subir documento
              </Button>
              {filesLoading ? <CircularProgress size={20} /> : null}
            </Stack>

            {projectFiles.length > 0 ? (
              <List dense disablePadding>
                {projectFiles.map((f) => (
                  <ListItem
                    key={f.fileName}
                    sx={{
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: "1px solid",
                      borderColor: "divider",
                      mb: 0.75,
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        backgroundColor: alpha(
                          theme.palette.primary.main,
                          isDark ? 0.08 : 0.04,
                        ),
                      },
                    }}
                    secondaryAction={
                      <Stack direction="row" spacing={0}>
                        <Tooltip title={isPdfFile(f) ? "Abrir PDF" : "Descargar"}>
                          <IconButton
                            size="small"
                            disabled={!f?.data}
                            onClick={() => openProjectFile(f)}
                            sx={{
                              color: theme.palette.primary.main,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1,
                                ),
                              },
                            }}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteProjectFile(f.fileName)}
                            sx={{
                              color: theme.palette.text.secondary,
                              "&:hover": {
                                backgroundColor: alpha(
                                  theme.palette.error.main,
                                  0.12,
                                ),
                                color: theme.palette.error.main,
                              },
                            }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    }
                  >
                    {f?.data ? (
                      <Box sx={{ mr: 1.25, flexShrink: 0 }}>
                        <LogisticaDocThumbnail
                          url={`data:${f.mimeType};base64,${f.data}`}
                          fileName={f.fileName}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: 52,
                          height: 52,
                          borderRadius: 1,
                          mr: 1.25,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: alpha(
                            theme.palette.primary.main,
                            0.1,
                          ),
                          color: theme.palette.primary.main,
                          flexShrink: 0,
                        }}
                      >
                        <InsertDriveFileOutlinedIcon fontSize="small" />
                      </Box>
                    )}
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 600,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            pr: 8,
                          }}
                        >
                          {f.fileName}
                        </Typography>
                      }
                      secondary={
                        <Chip
                          label={
                            f.tooLarge
                              ? "Archivo grande (solo en servidor)"
                              : f.mimeType || "—"
                          }
                          size="small"
                          sx={{
                            height: 16,
                            fontSize: "0.6rem",
                            fontWeight: 600,
                            backgroundColor: alpha(
                              theme.palette.text.primary,
                              0.06,
                            ),
                            color: theme.palette.text.secondary,
                            mt: 0.25,
                          }}
                        />
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : !filesLoading ? (
              <Box
                sx={{
                  py: 2.5,
                  textAlign: "center",
                  borderRadius: 1.5,
                  border: "1px dashed",
                  borderColor: "divider",
                  backgroundColor: alpha(
                    theme.palette.text.primary,
                    isDark ? 0.04 : 0.02,
                  ),
                }}
              >
                <InboxOutlinedIcon
                  sx={{
                    fontSize: 28,
                    color: theme.palette.text.disabled,
                    mb: 0.25,
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  No hay archivos en la carpeta del presupuesto.
                </Typography>
              </Box>
            ) : null}
          </>
        ) : idPresupuesto ? null : (
          <Typography variant="body2" color="text.secondary">
            Abre el drawer desde una fila con presupuesto para subir o listar
            documentos.
          </Typography>
        )}
      </Paper>
    </Stack>
  );
};

export default LogisticsSection;
